import { FormControl, InputLabel, MenuItem, Select, Tab, Tabs, ToggleButton } from "@mui/material";
import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "components/Tables/DataTable/DataTable";
import TableSkeleton from "components/TableSkeleton/TableSkeleton";
import { UserPermissions, UserStatus, useMeQuery } from "generated/graphql";
import useMyTasksTable from "hooks/tasks/useMyTasksTable";
import useGetOrganizationUsersLazy from "hooks/users/useGetOrganizationUsersLazy";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

const ASSIGNEE_FILTER_ALL = "all";

type Props = {
  status: "open" | "completed";
  onStatusChange?: (v: "open" | "completed") => void;
};

type ScopeParam = Parameters<typeof useMyTasksTable>[1];

export default function TasksList({ status, onStatusChange }: Props) {
  const { data: meData } = useMeQuery();
  const meId = meData?.me?.id;

  const permissions = meData?.me?.permissions ?? [];
  const canViewAllProposals = permissions.includes(UserPermissions.VIEW_ALL_PROPOSALS);

  const [getOrganizationUsers, { users: organizationUsers }] = useGetOrganizationUsersLazy();

  useEffect(() => {
    if (!canViewAllProposals) return;
    getOrganizationUsers({ first: 200, page: 1, status: UserStatus.ACTIVE });
  }, [canViewAllProposals, getOrganizationUsers]);

  const users = useMemo(() => {
    return organizationUsers;
  }, [organizationUsers]);

  const [assigneeId, setAssigneeId] = useState<string>("");

  const resolvedAssigneeId = useMemo(() => {
    if (!canViewAllProposals) return meId ?? "";
    if (assigneeId) return assigneeId;
    return meId ?? "";
  }, [assigneeId, canViewAllProposals, meId]);

  const scope = useMemo<ScopeParam>(() => {
    if (!meId) return undefined;

    if (!canViewAllProposals) return { userId: meId };

    if (resolvedAssigneeId === ASSIGNEE_FILTER_ALL) return { all: true };

    if (resolvedAssigneeId) return { userId: resolvedAssigneeId };

    return undefined;
  }, [resolvedAssigneeId, meId, canViewAllProposals]);

  const { loading, error, tableData, pagination, sorting } = useMyTasksTable(status, scope);
  const [dueTodayOnly, setDueTodayOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);

  const filteredTableData = useMemo(() => {
    if (!dueTodayOnly && !overdueOnly) return tableData;

    const today = dayjs();
    const rows = (tableData.rows || []).filter((row) => {
      if (!row?.dueAt) return false;
      const dueAt = dayjs(row.dueAt);
      const isDueToday = dueAt.isSame(today, "day");
      const isOverdue = dueAt.isBefore(today, "day");

      if (dueTodayOnly && overdueOnly) {
        return isDueToday || isOverdue;
      }

      if (dueTodayOnly) {
        return isDueToday;
      }

      return isOverdue;
    });

    return {
      ...tableData,
      rows,
    };
  }, [dueTodayOnly, overdueOnly, tableData]);

  const handleAssigneeChange = (value: string) => {
    setAssigneeId(value);
    pagination.setCurrentPage(1);
  };

  const handleStatusChange = (value: "open" | "completed") => {
    onStatusChange?.(value);
    pagination.setCurrentPage(1);
  };

  return (
    <>
      <MDBox px={2} pt={2} pb={1} display="flex" alignItems="center" justifyContent="space-between">
        <MDTypography variant="h6" fontWeight="medium">
          Tasks
        </MDTypography>

        <MDBox display="flex" alignItems="center" gap={2}>
          <Tabs
            value={status}
            onChange={(_, v) => handleStatusChange(v)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 36,
              "& .MuiTab-root": { minHeight: 36 },
            }}
          >
            <Tab label="Open" value="open" />
            <Tab label="Completed" value="completed" />
          </Tabs>
          <ToggleButton
            value="dueToday"
            selected={dueTodayOnly}
            onChange={() => {
              setDueTodayOnly((prev) => !prev);
              pagination.setCurrentPage(1);
            }}
            size="small"
          >
            Due Today
          </ToggleButton>
          <ToggleButton
            value="overdue"
            selected={overdueOnly}
            onChange={() => {
              setOverdueOnly((prev) => !prev);
              pagination.setCurrentPage(1);
            }}
            size="small"
          >
            Overdue
          </ToggleButton>
          {canViewAllProposals && (
            <FormControl variant="standard" sx={{ minWidth: 220 }}>
              <InputLabel id="tasks-assignee-filter-label">Assigned To</InputLabel>
              <Select
                labelId="tasks-assignee-filter-label"
                id="tasks-assignee-filter"
                value={resolvedAssigneeId || ""}
                label="Assigned To"
                onChange={(event) => handleAssigneeChange(String(event.target.value))}
              >
                <MenuItem value={ASSIGNEE_FILTER_ALL}>All Tasks</MenuItem>
                {meId && <MenuItem value={meId}>Me</MenuItem>}

                {users
                  .filter((user) => user.id && user.id !== meId)
                  .map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name ||
                        `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
                        user.email}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}
        </MDBox>
      </MDBox>

      {error ? (
        <div style={{ padding: "16px" }}>
          <MDAlert color="error">{error.message}</MDAlert>
        </div>
      ) : loading ? (
        <div style={{ padding: "16px" }}>
          <TableSkeleton numberOfLines={pagination.pageSize} />
        </div>
      ) : (
        <DataTable table={filteredTableData} pagination={pagination} sorting={sorting} isSorted />
      )}
    </>
  );
}
